import { useState, useEffect } from "react";
import { useToast } from "../../contexts/ToastContext";

import {
    Drawer,
    DrawerContent,
    DrawerNavigation,
} from "@progress/kendo-react-layout";
import { FaTimesCircle } from "react-icons/fa";
import "../Styles.css";
import { FormTextArea } from "../../components/FormComps/FormComponents";
import { TextMessageTemplate, getTextMessageTemplates, updateTextMessageTemplate } from "../../data/textMessageData";
import { canEditArea } from "../../helpers/areaAccess";

interface TextMessagesProps {
    allowedRoutes?: string[];
}

const TextMessages = (props: TextMessagesProps) => {
    /** TextMessages.Manage or an administrator (see areaAccess.ts). */
    const canEdit = canEditArea(props.allowedRoutes, "TextMessages");
    /** Is the sidebar expanded? */
    const [expanded, setExpanded] = useState(false);
    /** Table row style taken from table render */
    const trStyle = { backgroundColor: "rgb(245, 249, 251)", border: "1px solid rgb(216, 229, 237)" };
    /** Toast Hook */
    const { showToast } = useToast();
    /** The description text for the text message */
    const [description, setDescription] = useState("");
    /** Content length */
    const [contentLength, setContentLength] = useState(0);
    /** Templates */
    const [templates, setTemplates] = useState<TextMessageTemplate[]>([]);
    /** Selected Template */
    const [selectedTemplate, setSelectedTemplate] = useState<TextMessageTemplate | undefined>(undefined);

    /**
     * Handles the click event when a template is clicked.
     * @param {string} template - The template that was clicked.
     */
    const onTemplateClick = (template: TextMessageTemplate) => {
        setExpanded(true);
        setSelectedTemplate(template);
        setDescription(template.message);
        setContentLength(template.message.length)
    }

    /**
     * Handles the change event for the description input field.
     * Updates the description state and content length if the content length is less than or equal to 160 characters.
     * 
     * @param {string} content - The new content of the description input field.
     */
    const onDescriptionChange = (content: string) => {
        if(content.length <= 160) {
            setDescription(content);
            setContentLength(content.length);
        }
    }

    /**
     * Handles the form submission by calling the API to set items.
     */
    const onSubmit = async () => {
        if(selectedTemplate) {
            const updatedTemplate = { ...selectedTemplate, message: description };
            try {
                await updateTextMessageTemplate(updatedTemplate);
                showToast("The text message template has been updated successfully.", "success");
                getTemplates();
            } catch (error) {
                showToast("The text message template failed to update. Please try again.", "error");
            }
        }
    }

    /**
     * Retrieves the text message templates and sets them in the component state.
     * @returns {Promise<void>} A promise that resolves when the templates are retrieved and set.
     */
    const getTemplates = async () => { 
        const templates =  await getTextMessageTemplates();
        setTemplates(templates);
    }

    useEffect(() => {
        getTemplates();
    }, [])


    return (
        <div>
            <Drawer
                expanded={expanded}
                position={"end"}
                width={750}
                mode="overlay"
                animation={{
                    duration: 400,
                }}
                onOverlayClick={() => setExpanded(false)}
                onSelect={() => setExpanded(false)}
            >
                <DrawerContent>
                    <DrawerNavigation>
                        <div className="form__title__container">
                            <h1 className="form__title">{selectedTemplate?.name} Message Template</h1>
                            <FaTimesCircle
                                className="closeform"
                                onClick={() => setExpanded(false)}
                            />
                        </div>
                        <div style={{ padding: "20px" }}>
                            {!canEdit && <p className="k-form-hint">You have view-only access to text message templates.</p>}

                            <FormTextArea
                                name={'message'}
                                disabled={!canEdit}
                                value={description}
                                defaultValue={description}
                                label={'Message:'}
                                placeholder={'Enter a message:'}
                                onChange={(e: any) => {
                                    onDescriptionChange(e.value)
                                }}
                            />
                            {contentLength} {" / 160"}
                            {canEdit && (
                            <button
                                disabled={description === ""}
                                onClick={() => {
                                    onSubmit();
                                }}
                                className='form__edit__button accept'
                            >
                                Save changes
                            </button>
                            )}
                        </div>
                    </DrawerNavigation>
                    <div className="grid__title__container"><div className="grid__header__background"><div className="grid__title"><div>Text Message Templates</div></div></div></div>
                    <table className="grid__table">
                        <thead>
                            <tr>
                                <th style={{ color: "black", fontWeight: "bold" }}>Templates</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                templates.map(item => (
                                    <tr style={trStyle} key={item.textMessageTemplateId} onClick={() => onTemplateClick(item)}>
                                        <td>{item.name}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </DrawerContent>
            </Drawer>
        </div>
    );
};

export default TextMessages;
